import { useState } from 'react';
import { useAlert } from 'react-alert';

import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Add } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
  HigherEdOrg, HigherEdOrgStatus, OrgManagerInviteStatus, OrgManagerRole, sendInviteToOrgManager
} from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import {
  higherEdQueryKeys, useHigherEdManagersQuery, useHigherEdOrgManagerQuery
} from '@uplift/queries';
import { Button, FlexTable, Loader } from '@uplift/ui-v2';
import { capitalizeFirstCharacter, isExternalAdmin, isHigherEdAdmin, isSuperAdmin } from '@uplift/utils';

import { useAdminManagementColumns, useCareManagementColumns } from './hooks';
import { InviteManagerModal } from './modals';
import { InviteManagerFormValues } from './types';

export type ManagementTableProps = {
  orgData: HigherEdOrg;
  isCareApp?: boolean;
};

export const ManagementTable: React.VFC<ManagementTableProps> = ({ orgData, isCareApp }) => {
  const { id: orgId, status: orgStatus } = orgData;
  const [ inviteModalOpen, setInviteModalOpen ] = useState<boolean>(false);
  const [ accountExists, setAccountExists ] = useState<boolean>(false);
  const [ alertMessage, setAlertMessage ] = useState<string>('');

  const queryClient = useQueryClient();
  const l10n = useL10n();
  const alert = useAlert();
  const { classes } = useManagementTableStyles();

  const me = useCurrentuser();
  const isSuperAdminOrHigherEdAdmin = isSuperAdmin(me) || isHigherEdAdmin(me);
  const isOrgManager = isExternalAdmin(me);
  const { data: currentManagerData } = useHigherEdOrgManagerQuery(me?.id, { enabled: isOrgManager, suspense: true });
  const isOrgOwner = currentManagerData?.role === OrgManagerRole.owner;
  const isOrgStaff = currentManagerData?.role === OrgManagerRole.staff;
  const isOrgAdmin = currentManagerData?.role === OrgManagerRole.admin;

  const { data: managers, isLoading } = useHigherEdManagersQuery(orgData?.id);
  const filteredManagers = managers?.filter((manager) => manager.inviteStatus !== OrgManagerInviteStatus.revoked);

  const showInviteManagerButton = isSuperAdminOrHigherEdAdmin || !isOrgStaff;
  const disableInviteManagerButton = orgStatus === HigherEdOrgStatus.inactive;

  const careColumns = useCareManagementColumns(orgId, managers, isOrgOwner);
  const adminColumns = useAdminManagementColumns(orgId, managers);
  const managementTableColumns = isCareApp ? careColumns : adminColumns;

  const handleInviteManager = async (values: InviteManagerFormValues) => {
    try {
      await sendInviteToOrgManager({
        orgId,
        role: values.role,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      });
      queryClient.invalidateQueries(higherEdQueryKeys.managers(orgId));
      setInviteModalOpen(false);
      alert.success(l10n.admin.inviteSentTo(values.email));
    } catch (error) {
      const errorMessage = error.response.data.error || 'Invite could not be send. Please try again or contact help@joinuplift.co for support.';

      if (errorMessage === 'Email in use') {
        setAccountExists(true);
        setAlertMessage('Unable to invite users with existing UpLift accounts.');
      } else if (errorMessage === 'Already accepted invite') {
        setAccountExists(true);
        setAlertMessage(`${capitalizeFirstCharacter(values.email)} is already an active manager.`);
      } else if (errorMessage === 'Unable to invite deactivated users') {
        setAccountExists(true);
        const message = isOrgOwner
          ? `Unable to invite deactivated users. Reactivate ${values.email} from the manager list.`
          : `Unable to invite deactivated users. Ask the owner to reactivate ${values.email} from the manager list.`;

        setAlertMessage(message);
      } else if (errorMessage === 'Already invited') {
        setAccountExists(true);
        setAlertMessage(`${values.email} has already been invited.`);
      } else {
        alert.error(errorMessage);
      }
    }
  };

  const handleModalClose = () => {
    setAccountExists(false);
    setInviteModalOpen(false);
  };

  if (isLoading) {
    return (
      <Box height="100%" width="100%" display="flex" justifyContent="center" alignItems="center">
        <Loader />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {inviteModalOpen && (
        <InviteManagerModal
          onSubmit={handleInviteManager}
          onCancel={handleModalClose}
          isOrgAdmin={isOrgAdmin}
          accountExists={accountExists}
          setAccountExists={setAccountExists}
          alertMessage={alertMessage}
        />
      )}
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" flexDirection="column">
          <Typography variant="h4">{isCareApp ? 'Managers' : 'Management'}</Typography>
        </Box>
        {showInviteManagerButton && (
          <Button
            variant={isCareApp ? 'contained' : 'text'}
            color="secondary"
            disabled={disableInviteManagerButton}
            startIcon={<Add />}
            onClick={() => setInviteModalOpen(true)}
            testId="invite-manager-button"
            style={{ alignSelf: 'flex-end' }}
          >
            Invite Manager
          </Button>
        )}
      </Box>
      <Paper elevation={2}>
        <FlexTable
          rows={filteredManagers || []}
          columns={managementTableColumns}
          hidePagination={managers?.length <= 10}
          rowsPerPageOptions={[ 10, 20, 30 ]}
          initialSort={{
            colId: 'user',
            type: 'asc',
          }}
        />
      </Paper>
    </Box>
  );
};

const useManagementTableStyles = makeStyles({ name: 'ManagementTable' })((theme)=> ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(2),
  }
}));
